import { useCallback, useState } from "react";

import type {
	LocalColumn,
	ScheduleColumn,
	ScheduleColumnType,
	ScheduleRow,
} from "./schedule-of-rates.types";
import {
	formatCurrencyValue,
	fromLocalColumns,
	generateLocalColumnId,
	generateRowId,
	generateSortId,
	toLocalColumns,
} from "./schedule-utils";

export interface UseScheduleEditorProps {
	initialColumns: ScheduleColumn[];
	initialRows: ScheduleRow[];
}

export interface UseScheduleEditorReturn {
	columns: LocalColumn[];
	rows: ScheduleRow[];
	// Column operations
	addColumn: (type: ScheduleColumnType) => void;
	removeColumn: (localId: string) => void;
	updateColumnLabel: (localId: string, label: string) => void;
	updateColumnType: (localId: string, type: ScheduleColumnType) => void;
	reorderColumns: (sourceIndex: number, destIndex: number) => void;
	moveColumn: (localId: string, direction: "up" | "down") => void;
	// Row operations
	addRow: () => void;
	removeRow: (rowId: number) => void;
	updateCellValue: (rowId: number, colId: number, value: string) => void;
	formatCurrencyCell: (rowId: number, colId: number) => void;
	// Validation
	validationErrors: Set<string>;
	validate: () => boolean;
	clearValidationError: (key: string) => void;
	// State management
	reset: (columns: ScheduleColumn[], rows: ScheduleRow[]) => void;
	getColumns: () => ScheduleColumn[];
	getRows: () => ScheduleRow[];
}

const remapRowValues = (
	rows: ScheduleRow[],
	idMap: Record<number, number>
): ScheduleRow[] => {
	return rows.map((row) => {
		const newValues: Record<number, string> = {};
		for (const [oldKey, value] of Object.entries(row.values)) {
			const newKey = idMap[Number(oldKey)];
			if (newKey !== undefined) {
				newValues[newKey] = value;
			}
		}
		return { ...row, values: newValues };
	});
};

export const useScheduleEditor = ({
	initialColumns,
	initialRows,
}: UseScheduleEditorProps): UseScheduleEditorReturn => {
	const [columns, setColumns] = useState<LocalColumn[]>(
		() => toLocalColumns(initialColumns)
	);
	const [rows, setRows] = useState<ScheduleRow[]>(initialRows);
	const [validationErrors, setValidationErrors] = useState<Set<string>>(
		new Set()
	);

	// --- Column operations ---

	const addColumn = useCallback(
		(columnType: ScheduleColumnType) => {
			const nextSortOrder = generateSortId(columns);
			const newCol: LocalColumn = {
				id: nextSortOrder,
				label: `Column ${columns.length + 1}`,
				columnType,
				sortOrder: nextSortOrder,
				localId: generateLocalColumnId(),
			};
			setColumns((prev) => [...prev, newCol]);
		},
		[columns]
	);

	const removeColumn = useCallback(
		(localId: string) => {
			const col = columns.find((c) => c.localId === localId);
			if (!col) {
				return;
			}
			setColumns((prev) => prev.filter((c) => c.localId !== localId));
			setRows((prev) =>
				prev.map((row) => {
					const newValues = { ...row.values };
					delete newValues[col.id];
					return { ...row, values: newValues };
				})
			);
		},
		[columns]
	);

	const updateColumnLabel = useCallback(
		(localId: string, label: string) => {
			setColumns((prev) =>
				prev.map((c) => (c.localId === localId ? { ...c, label } : c))
			);
		},
		[]
	);

	const updateColumnType = useCallback(
		(localId: string, columnType: ScheduleColumnType) => {
			setColumns((prev) =>
				prev.map((c) =>
					c.localId === localId ? { ...c, columnType } : c
				)
			);
		},
		[]
	);

	const reorderColumns = useCallback(
		(sourceIndex: number, destIndex: number) => {
			if (sourceIndex === destIndex) {
				return;
			}
			setColumns((prev) => {
				const reordered = [...prev];
				const [moved] = reordered.splice(sourceIndex, 1);
				reordered.splice(destIndex, 0, moved);

				// Build old→new id mapping and reassign sortOrder = id
				const idMap: Record<number, number> = {};
				const updated = reordered.map((col, idx) => {
					const newId = idx + 1;
					idMap[col.id] = newId;
					return { ...col, sortOrder: newId, id: newId };
				});

				// Re-key row cell values to match new column ids
				setRows((prevRows) => remapRowValues(prevRows, idMap));

				return updated;
			});
		},
		[]
	);

	const moveColumn = useCallback(
		(localId: string, direction: "up" | "down") => {
			const idx = columns.findIndex((c) => c.localId === localId);
			if (idx === -1) {
				return;
			}
			const targetIdx = direction === "up" ? idx - 1 : idx + 1;
			if (targetIdx < 0 || targetIdx >= columns.length) {
				return;
			}
			reorderColumns(idx, targetIdx);
		},
		[columns, reorderColumns]
	);

	// --- Row operations ---

	const addRow = useCallback(() => {
		const newRow: ScheduleRow = {
			id: generateRowId(),
			sortOrder: rows.length + 1,
			values: {},
		};
		setRows((prev) => [...prev, newRow]);
	}, [rows.length]);

	const removeRow = useCallback((rowId: number) => {
		setRows((prev) => prev.filter((r) => r.id !== rowId));
	}, []);

	const updateCellValue = useCallback(
		(rowId: number, colId: number, value: string) => {
			setRows((prev) =>
				prev.map((row) => {
					if (row.id === rowId) {
						return {
							...row,
							values: { ...row.values, [colId]: value },
						};
					}
					return row;
				})
			);
			if (value.trim()) {
				setValidationErrors((prev) => {
					const next = new Set(prev);
					next.delete(`${rowId}-${colId}`);
					return next;
				});
			}
		},
		[]
	);

	const formatCurrencyCell = useCallback(
		(rowId: number, colId: number) => {
			setRows((prev) =>
				prev.map((row) => {
					if (row.id === rowId) {
						const value = row.values[colId] ?? "";
						if (value) {
							return {
								...row,
								values: {
									...row.values,
									[colId]: formatCurrencyValue(value),
								},
							};
						}
					}
					return row;
				})
			);
		},
		[]
	);

	// --- Validation ---

	const validate = useCallback((): boolean => {
		if (columns.length === 0 || rows.length === 0) {
			return true;
		}
		const errors = new Set<string>();
		rows.forEach((row) => {
			columns.forEach((col) => {
				const value = row.values[col.id] ?? "";
				if (!value.trim()) {
					errors.add(`${row.id}-${col.id}`);
				}
			});
		});
		if (errors.size > 0) {
			setValidationErrors(errors);
			return false;
		}
		setValidationErrors(new Set());
		return true;
	}, [columns, rows]);

	const clearValidationError = useCallback((key: string) => {
		setValidationErrors((prev) => {
			const next = new Set(prev);
			next.delete(key);
			return next;
		});
	}, []);

	// --- State management ---

	const reset = useCallback(
		(newColumns: ScheduleColumn[], newRows: ScheduleRow[]) => {
			setColumns(toLocalColumns(newColumns));
			setRows(newRows);
			setValidationErrors(new Set());
		},
		[]
	);

	const getColumns = useCallback((): ScheduleColumn[] => {
		return fromLocalColumns(columns);
	}, [columns]);

	const getRows = useCallback((): ScheduleRow[] => {
		return rows;
	}, [rows]);

	return {
		columns,
		rows,
		addColumn,
		removeColumn,
		updateColumnLabel,
		updateColumnType,
		reorderColumns,
		moveColumn,
		addRow,
		removeRow,
		updateCellValue,
		formatCurrencyCell,
		validationErrors,
		validate,
		clearValidationError,
		reset,
		getColumns,
		getRows,
	};
};
